import { useState } from 'react';
import Image from 'next/image';
import NextLink from 'next/link';
import { Navigation, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Button, Container, Icon, Link, SectionText } from '@/atoms';
import PopUpGaleria from '@/atoms/multimedia/Galeria/PopUpGaleria';
import { ContactFormBase } from '@/organisms';

import RightArrow from '@/assets/icons/DerechaSinBorde.svg';
import LeftArrow from '@/assets/icons/IzquierdaSinBorde.svg';

import type { FunctionComponent } from 'react';
import type { TypeContactFormProps } from './types';

/**
 * ContactForm
 */

export const ContactForm: FunctionComponent<TypeContactFormProps> = ({
  className = '',
  caption,
  formValues,
  title,
  landing,
  background,
  desc,
  index,
  textColor,
  id,
  features,
  button,
  link,
  opensBookingPopup,
  carousel,
}: TypeContactFormProps) => {
  const [showLavandaPopUp, setShowLavandaPopUp] = useState(false);
  const [selectedImg, setselectedImg] = useState<number>(0);
  const [openGallery, setOpenGallery] = useState(false);

  const getFeatureColumns = () => {
    if (!features || features.length < 4) return [features ?? []];

    const columns: (typeof features)[] = [[], []];
    features?.forEach((feat, index) => {
      columns?.[index <= Math.ceil(features.length / 2 - 1) ? 0 : 1]?.push(
        feat
      );
    });

    return columns;
  };

  return (
    <>
      <style scoped>
        {`
    .backimage {
      background-image: linear-gradient(rgba(0, 0, 0, 0.1), rgba(255, 247, 243, 0.4)), url(${background.backgroundImageMobile});
      background-size: auto;
      background-position: top center;
      
        @media (min-width: 750px) {
          background-image: linear-gradient(rgba(0, 0, 0, 0.1), rgba(255, 247, 243, 0.1)), url(${background.backgroundImage}) !important;
          background-size: cover !important;
          background-position: top left;
        }
        
        @media (min-width: 1024px) {
          background-position: unset;
        }
    }
  `}
      </style>
      {carousel && (
        <PopUpGaleria
          images={carousel}
          setState={setOpenGallery}
          state={openGallery}
          initialIndex={selectedImg}
        />
      )}
      <div
        className={`backimage relative ${
          landing ? '' : `bg-${background.backgroundColorTop}`
        }`}
      >
        <div
          className={`absolute bottom-0 left-0 right-0 top-0 ${
            landing
              ? `bg-[rgba(0,0,0,0.4)]`
              : `bg-${background.backgroundColorTop}`
          } }`}
        />
        <Container
          id={id}
          index={index}
          backgroundColorBottom={background.backgroundColorBottom}
          backgroundColorTop={background.backgroundColorTop}
          backgroundColorBottomMobile={background.backgroundColorBottomMobile}
          backgroundColorTopMobile={background.backgroundColorTopMobile}
          cutLevel={background.cutLevel}
          cutLevelMobile={background.cutLevelMobile}
          hasCutLevel={background.hasCutLevel}
          className={`!py-14 lg:!py-20 ${
            landing
              ? 'flex w-full !max-w-none flex-col gap-8 !py-4 px-0 sm:px-5 md:!py-20 md:pr-4 lg:gap-16'
              : 'flex flex-col gap-5'
          } ${className}`}
        >
          <div className="relative mx-auto flex max-w-[100%] flex-col gap-8 md:max-w-[1450px] lg:gap-14">
            {/* TEXT, LINKS AND FORM */}
            <div
              className={`flex w-full flex-col items-start justify-between ${
                landing ? 'lg:flex-row' : ''
              } `}
            >
              {/* TEXT AND LINKS */}
              <div
                className={`flex flex-col justify-between gap-6 px-4 sm:gap-10 ${
                  landing ? 'lg:w-3/5' : 'w-full'
                } `}
              >
                {/* TEXT AND BULLETS */}
                <div className="flex flex-col">
                  <SectionText
                    direction="column"
                    textAlign={landing ? 'start' : 'center'}
                    textAlignMobile="start"
                    variant={textColor}
                    caption={caption}
                    title={title}
                    className={
                      landing
                        ? '![&>div>div]:mb-0 !mb-0 w-full justify-start !gap-2 lg:[&>div>div>h2]:text-heading--h1'
                        : 'items-center'
                    }
                    desc={desc}
                  />
                  {/* FEATURE BULLETS */}
                  {features && (
                    <ul className="grid w-fit text-white md:grid-cols-2 md:gap-x-4">
                      {getFeatureColumns().map((col, colIndex) => (
                        <div
                          key={colIndex}
                          className="flex flex-col md:gap-y-2"
                        >
                          {col.map((feat, index) => {
                            if (feat && feat !== '') {
                              return (
                                <li
                                  className="flex w-fit items-center gap-2"
                                  key={`feat-${index}`}
                                >
                                  <Icon
                                    className="w-6 min-w-[24px] text-white"
                                    src="CheckmarkCircle"
                                  />
                                  <span className="font-secondary">{feat}</span>
                                </li>
                              );
                            }
                          })}
                        </div>
                      ))}
                    </ul>
                  )}
                </div>
                {/* BUTTON - LINK */}
                <div className="flex flex-col items-center justify-center gap-6 md:flex-row  md:justify-normal  md:gap-7 ">
                  {!opensBookingPopup
                    ? button?.src &&
                      button.label &&
                      button.variant &&
                      !opensBookingPopup && (
                        <NextLink href={button.src} passHref prefetch={false}>
                          <Button
                            className="font-bold"
                            size="large"
                            as="a"
                            variant={button.variant}
                          >
                            {button.label}
                          </Button>
                        </NextLink>
                      )
                    : button?.label &&
                      button?.src &&
                      button?.variant && (
                        <Button
                          className="font-bold"
                          size="large"
                          variant={button.variant}
                          onClick={() => setShowLavandaPopUp(!showLavandaPopUp)}
                        >
                          {button.label}
                        </Button>
                      )}
                  {link?.src && link.label && (
                    <NextLink href={link?.src} passHref prefetch={false}>
                      <Link arrow={true} variant={link.variant}>
                        {link?.label}
                      </Link>
                    </NextLink>
                  )}
                </div>
              </div>
              <ContactFormBase
                {...formValues}
                landing={landing}
                className="hidden lg:block"
              />
            </div>
            {/* GALLERY */}
            <div className="flex w-full flex-col items-center gap-4 px-4 lg:gap-8 lg:px-16">
              <Swiper
                className="w-full"
                slidesPerView="auto"
                breakpoints={{
                  0: {
                    slidesPerView: 1,
                  },
                  360: {
                    slidesPerView: 'auto',
                  },
                }}
                spaceBetween={30}
                modules={[Navigation, Scrollbar]}
                navigation={{
                  enabled: true,
                  nextEl: '.nextImg',
                  prevEl: '.prevImg',
                }}
                scrollbar={{
                  hide: false,
                  draggable: true,
                  enabled: true,
                  el: '.heroScrollbar',
                }}
              >
                {carousel?.map((img: string, index: number) => (
                  <SwiperSlide
                    key={index}
                    className="w-full cursor-pointer sm:max-w-fit"
                    onClick={() => {
                      setselectedImg(index);
                      setOpenGallery(true);
                    }}
                  >
                    <Image
                      src={img}
                      width={250}
                      height={164}
                      alt="Hero gallery element"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              <div
                className={`heroScrollbar h-1 w-4/5 bg-[#ffffff61] [&>.swiper-scrollbar-drag]:bg-white ${
                  openGallery ? 'hidden' : ''
                }`}
              />
            </div>
            {/* GALLERY BUTTONS */}
            <LeftArrow
              className="prevImg absolute bottom-[110px] hidden w-8 cursor-pointer text-primary-500 lg:block"
              role="button"
            />
            <RightArrow
              className="nextImg absolute bottom-[110px] right-0 hidden w-8 cursor-pointer text-primary-500 lg:block"
              role="button"
            />
          </div>

          <ContactFormBase
            {...formValues}
            landing={landing}
            className="block lg:hidden"
          />
        </Container>
      </div>
    </>
  );
};

ContactForm.displayName = 'ContactForm';
